import React, { useState } from 'react'
import { FontPairing } from '../pairings-data'

interface PairingCardProps {
  pairing: FontPairing
  sampleText: string
  displayFontSize: number
  bodyFontSize: number
  isLoaded: boolean
  categoryLabel?: string
  isFavorited?: boolean
  onToggleFavorite?: () => void
  authenticated?: boolean
}

export default function PairingCard({
  pairing,
  sampleText,
  displayFontSize,
  bodyFontSize,
  isLoaded,
  categoryLabel,
  isFavorited,
  onToggleFavorite,
  authenticated,
}: PairingCardProps) {
  const [copied, setCopied] = useState(false)

  const copyCss = async () => {
    const displayFamily = encodeURIComponent(pairing.displayFont)
    const bodyFamily = encodeURIComponent(pairing.bodyFont)

    const css = `/* ${pairing.name || `${pairing.displayFont} + ${pairing.bodyFont}`} */
@import url('https://fonts.googleapis.com/css2?family=${displayFamily}:wght@400;700&family=${bodyFamily}:wght@400;500;700&display=swap');

/* Display font */
.display-font {
  font-family: '${pairing.displayFont}', sans-serif;
}

/* Body font */
.body-font {
  font-family: '${pairing.bodyFont}', sans-serif;
}`

    await navigator.clipboard.writeText(css)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleFavoriteClick = () => {
    if (!authenticated) {
      window.location.href = `/users/sign-in?return_to=${encodeURIComponent(window.location.pathname)}`
      return
    }
    onToggleFavorite?.()
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 hover:shadow-md transition-shadow">
      {/* Font previews */}
      <div
        className="transition-opacity duration-300"
        style={{ opacity: isLoaded ? 1 : 0.5 }}
      >
        <div
          className="text-gray-900 leading-tight break-words truncate"
          style={{
            fontFamily: isLoaded ? `'${pairing.displayFont}', sans-serif` : 'sans-serif',
            fontSize: `${displayFontSize}px`,
          }}
        >
          {pairing.name || `${pairing.displayFont} + ${pairing.bodyFont}`}
        </div>
        <div
          className="text-gray-600 leading-snug break-words mt-1 line-clamp-2"
          style={{
            fontFamily: isLoaded ? `'${pairing.bodyFont}', sans-serif` : 'sans-serif',
            fontSize: `${bodyFontSize}px`,
          }}
        >
          {sampleText}
        </div>
      </div>

      {/* Footer: metadata + actions on one line */}
      <div className="flex items-center justify-between mt-2 gap-2">
        <div className="flex items-center gap-2 min-w-0 text-xs text-gray-400">
          {categoryLabel && (
            <>
              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] whitespace-nowrap flex-shrink-0">
                {categoryLabel}
              </span>
            </>
          )}
          <span className="truncate">
            <span className="text-gray-600 font-medium">{pairing.displayFont}</span>
            {' + '}
            <span className="text-gray-600 font-medium">{pairing.bodyFont}</span>
          </span>
          {pairing.name && (
            <span className="hidden sm:inline text-gray-400 truncate">&middot; {pairing.name}</span>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {onToggleFavorite !== undefined && (
            <button
              onClick={handleFavoriteClick}
              className="p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer group"
              title={authenticated ? (isFavorited ? 'Remove from favorites' : 'Add to favorites') : 'Sign in to favorite'}
            >
              {isFavorited ? (
                <HeartFilledIcon />
              ) : (
                <HeartOutlineIcon />
              )}
            </button>
          )}
          <button
            onClick={copyCss}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-500 rounded hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            {copied ? (
              <>
                <CheckIcon />
                Copied
              </>
            ) : (
              <>
                <CopyIcon />
                CSS
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

function CopyIcon() {
  return (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function HeartFilledIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
      <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
    </svg>
  )
}

function HeartOutlineIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  )
}
